import React, { useEffect } from 'react';

import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import {
  BasicModalWithFooter,
  FluidAsset,
  Icon,
  PrimaryButton,
  SecondaryButton,
  TextLarge,
  TextRegular,
  Title3,
  UtilsBlock,
} from '@hz-design-system/web-ui';

import { BuyerModalLabel, FavouriteCategory, AnalyticsAction, type TrackEvent } from '../analytics';
import { LS_HAS_BUYER_SEEN_MODAL_KEY, notificationSettingsUrl } from '../constants';
import { hasBuyerSeenFavoritesModal } from '../utils';

import Classes from './FavouriteConnectInformationModal.module.scss';

interface ButtonProps {
  t: ReturnType<typeof useI18nContext>['t'];
  onAccept: () => void;
  onManageSettings: () => void;
}

const getButtons = ({ t, onAccept, onManageSettings }: ButtonProps) => {
  return [
    {
      element: (
        <SecondaryButton onClick={onManageSettings}>{t('favouritesUi:infoModal.manageSettings')}</SecondaryButton>
      ),
    },
    {
      element: <PrimaryButton onClick={onAccept}>{t('favouritesUi:infoModal.gotIt')}</PrimaryButton>,
    },
  ];
};

type Props = {
  isOpen: boolean;
  tenant?: string;
  onClose: () => void;
  onTrack?: TrackEvent;
};

const FavouriteConnectBuyerModal = ({ isOpen, onClose, onTrack }: Props) => {
  const { t } = useI18nContext();

  // user should only see this modal once peer device.
  const hasSeenModal = hasBuyerSeenFavoritesModal();

  useEffect(() => {
    if (!hasSeenModal && isOpen) {
      onTrack?.({
        action: AnalyticsAction.BUYER_MODAL,
        category: FavouriteCategory,
        label: BuyerModalLabel.IMPRESSION,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hasSeenModal]);

  if (hasSeenModal) {
    return null;
  }

  const setModalAsSeen = () => {
    localStorage.setItem(LS_HAS_BUYER_SEEN_MODAL_KEY, 'true');
  };

  const handleClose = () => {
    onTrack?.({ action: AnalyticsAction.BUYER_MODAL, category: FavouriteCategory, label: BuyerModalLabel.DISMISS });
    setModalAsSeen();
    onClose();
  };

  const handleManageSettings = () => {
    onTrack?.({
      action: AnalyticsAction.BUYER_MODAL,
      category: FavouriteCategory,
      label: BuyerModalLabel.MANAGE_NOTIFICATIONS,
    });
    setModalAsSeen();
    onClose();
    window.location.href = notificationSettingsUrl;
  };

  return (
    <BasicModalWithFooter
      isOpen={isOpen}
      size="m"
      onClose={handleClose}
      buttonsAlignmentMobile="stacked"
      buttonsAlignmentDesktop="rightAligned"
      buttons={getButtons({
        t,
        onAccept: handleClose,
        onManageSettings: handleManageSettings,
      })}
    >
      <Title3>{t('favouritesUi:infoModal.title')}</Title3>

      <UtilsBlock className={Classes.imageContainer}>
        <FluidAsset name="interaction-favourite" className={Classes.imageAsset} />
      </UtilsBlock>
      <TextLarge className={Classes.message}>{t('favouritesUi:infoModal.message')}</TextLarge>

      <UtilsBlock className={Classes.turnOffMessageContainer}>
        <Icon name="lightbulb" className={Classes.lightbulb} />
        <TextRegular>{t('favouritesUi:infoModal.turnOffMessage')}</TextRegular>
      </UtilsBlock>
    </BasicModalWithFooter>
  );
};

export default FavouriteConnectBuyerModal;
